import { ActionBar, Button, ModalV2 } from '@components'
import { PermissionSet, useBusinessContext, useFeature, usePermission } from '@context'
import { exportContactsCsv } from '@query'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaDownload, FaPlus } from 'react-icons/fa'
import { ContactFormModal } from './ContactFormModal'
import { ContactImportWizard } from '@containers/CSVImportWizard/ContactImportWizard.tsx'
import { FaFileCsv } from 'react-icons/fa6'
import { useOpenCreateFromQuery } from '@hooks'

interface Props {
  onSearch: (value: string) => void
  searchTerm: string
}

export const ContactsActionBar: React.FC<Props> = ({ onSearch, searchTerm }) => {
  const [t] = useTranslation()
  const [showCreate, setShowCreate] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const { enabled: bulkInvoicing } = useFeature('bulk_invoicing')
  const { slug } = useBusinessContext()
  const hasEditPermission = usePermission(PermissionSet.CONTACT_EDIT)

  useOpenCreateFromQuery(() => setShowCreate(true), hasEditPermission)

  const onExportClick = () =>
    toast.promise(exportContactsCsv({ slug }, { search: searchTerm || undefined }), {
      loading: t('general.loading'),
      success: t('general.success'),
      error: t('general.error')
    })

  const primaryButtons = [
    <Button key="new-contact-button" icon={<FaPlus />} onClick={() => setShowCreate(true)}>
      {t('contacts.newContact')}
    </Button>,
    // Activate the import button only if the bulk invoicing feature is enabled
    ...(bulkInvoicing
      ? [
          <Button key="csv-import" icon={<FaFileCsv />} onClick={() => setShowImport(true)}>
            {t('contacts.importButton')}
          </Button>
        ]
      : []),
    <Button key="csv-export" icon={<FaDownload />} onClick={onExportClick}>
      {t('contacts.exportButton')}
    </Button>
  ]

  return (
    <>
      <ActionBar
        searchPlaceholder={t('contacts.searchContacts')}
        onSearch={search => onSearch(search)}
        primaryButtons={hasEditPermission && primaryButtons}
      />
      <ContactFormModal isVisible={showCreate} handleOnClose={() => setShowCreate(false)} />

      <ModalV2
        isVisible={showImport}
        header={t('contacts.importTitle')}
        handleOnClose={() => setShowImport(false)}
        width={800}
        height={900}
      >
        <ContactImportWizard onComplete={() => setShowImport(false)} />
      </ModalV2>
    </>
  )
}
